import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Chip,
} from "@mui/material";
import GenericModal from "../../components/GenericModal";
import GenericButton from "../../components/GenericButton";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import { fetchConsignment } from "../../store/slices/consignmentSlice";
import {
  getConsignmentTracking,
  ConsignmentTrackingDetails,
} from "../../services/consignmentApi";
import { useTheme } from "../../contexts/ThemeContext";

interface ConsignmentViewProps {
  open: boolean;
  onClose: () => void;
  consignmentId: number | null;
}

const ConsignmentView: React.FC<ConsignmentViewProps> = ({
  open,
  onClose,
  consignmentId,
}) => {
  const { theme } = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const { selected, loading } = useSelector((s: RootState) => s.consignments);
  const [, setTracking] = useState<ConsignmentTrackingDetails | null>(null);
  const [loadingTracking, setLoadingTracking] = useState(false);

  useEffect(() => {
    if (open && consignmentId) {
      dispatch(fetchConsignment(consignmentId));
      (async () => {
        setLoadingTracking(true);
        try {
          const t = await getConsignmentTracking(consignmentId);
          setTracking(t);
        } catch (e) {
          setTracking(null);
        } finally {
          setLoadingTracking(false);
        }
      })();
    }
  }, [open, consignmentId, dispatch]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-PK", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <GenericModal
      open={open}
      onClose={onClose}
      title={
        selected
          ? `Consignment #${selected.consignment_no}`
          : "Consignment Details"
      }
      subtitle={selected ? formatDate(selected.created_at) : undefined}
      actions={[
        <GenericButton key="close" variant="primary" onClick={onClose}>
          Close
        </GenericButton>,
      ]}
    >
      {!selected || loading ? (
        <Typography variant="body2">Loading...</Typography>
      ) : (
        <Box>
          {/* Status Badge */}
          <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h6">Status:</Typography>
            <Chip
              label={selected.status?.name || "Unknown"}
              color="primary"
              variant="outlined"
            />
          </Box>

          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  p: 1,
                  bgcolor: theme.palette.action.hover,
                  borderRadius: 1,
                  color: theme.palette.text.primary,
                }}
              >
                Basic Information
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Consignment No:</strong> {selected.consignment_no}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Booking Date:</strong>{" "}
                  {formatDate(selected.created_at)}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Service Type:</strong> {selected.service_type}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Origin:</strong> {selected.originCity?.code} -{" "}
                  {selected.originCity?.name}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Destination:</strong> {selected.destinationCity?.code}{" "}
                  - {selected.destinationCity?.name}
                </Typography>
                {selected.booking_type === "RETAIL" ? (
                  <>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Client:</strong> {selected.user?.name} (
                      {selected.user?.clientProfile?.company_name})
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Good's Description:</strong>{" "}
                      {selected.goods_description || "-"}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Order ID:</strong> {selected.orderId || "-"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Branch:</strong> {selected.branch?.name || "-"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Special Instructions:</strong>{" "}
                      {selected.special_instructions || "-"}
                    </Typography>
                  </>
                )}
              </Box>
            </Grid>

            {/* Package Information */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  p: 1,
                  bgcolor: theme.palette.action.hover,
                  borderRadius: 1,
                  color: theme.palette.text.primary,
                }}
              >
                Package Information
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Pieces:</strong> {selected.pieces || "-"}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Height / Width / Length (CM):</strong>{" "}
                  {selected.height} / {selected.width} / {selected.length}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Dense Weight (KGs):</strong> {selected.weight || "-"}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Volumetric Weight (KGs):</strong>{" "}
                  {selected.volumetric_weight || "-"}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Chargeable Weight (KGs):</strong>{" "}
                  {selected.chargeable_weight || "-"}
                </Typography>
                {selected.booking_type === "RETAIL" ? (
                  <>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Price (Rs):</strong>{" "}
                      {selected.price ? formatCurrency(selected.price) : "-"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Discount (Rs):</strong>{" "}
                      {selected.discount
                        ? formatCurrency(selected.discount)
                        : "-"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Total Price (Rs):</strong>{" "}
                      {selected.total_price
                        ? formatCurrency(selected.total_price)
                        : "-"}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>COD Amount (Rs):</strong>{" "}
                      {selected.cod_amount
                        ? formatCurrency(selected.cod_amount)
                        : "-"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>COD Type:</strong> {selected.cod_type || "-"}
                    </Typography>
                  </>
                )}
              </Box>
            </Grid>

            {/* Consignee Information */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  p: 1,
                  bgcolor: theme.palette.action.hover,
                  borderRadius: 1,
                  color: theme.palette.text.primary,
                }}
              >
                Consignee
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Name:</strong> {selected.consignee?.name || "-"}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Phone Number:</strong>{" "}
                  {selected.consignee?.phone || "-"}
                </Typography>
                {selected.consignee?.phone2 && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Second Phone Number:</strong>{" "}
                    {selected.consignee.phone2}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Address:</strong> {selected.consignee?.address || "-"}
                </Typography>
              </Box>
            </Grid>

            {/* Consigner Information */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  p: 1,
                  bgcolor: theme.palette.action.hover,
                  borderRadius: 1,
                  color: theme.palette.text.primary,
                }}
              >
                Consigner
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Name:</strong> {selected.consigner?.name || "-"}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Phone Number:</strong>{" "}
                  {selected.consigner?.phone || "-"}
                </Typography>
                {selected.consigner?.phone2 && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Second Phone Number:</strong>{" "}
                    {selected.consigner.phone2}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Address:</strong> {selected.consigner?.address || "-"}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>CNIC:</strong> {selected.consigner?.cnic || "-"}
                </Typography>
              </Box>
            </Grid>

            {/* Node Information */}
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  p: 1,
                  bgcolor: theme.palette.action.hover,
                  borderRadius: 1,
                  color: theme.palette.text.primary,
                }}
              >
                Node Information
              </Typography>
              <Grid container spacing={2} sx={{ pl: 2 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      First Mile (FM) Node
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selected.first_mile_node?.name || "-"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Where consignment was created
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: "primary.main",
                      borderRadius: 1,
                      bgcolor: "primary.50",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="primary"
                      sx={{ mb: 1 }}
                    >
                      Current (CN) Node
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selected.current_node?.name || "-"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Current location
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Last Mile (LM) Node
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selected.last_mile_node?.name || "-"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Delivery destination
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            {/* Tracking History */}
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  p: 1,
                  bgcolor: theme.palette.action.hover,
                  borderRadius: 1,
                  color: theme.palette.text.primary,
                }}
              >
                Tracking
              </Typography>
              {loadingTracking ? (
                <Typography variant="body2">Loading tracking...</Typography>
              ) : selected.trackings && selected.trackings.length > 0 ? (
                <Paper variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Status</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Description</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selected.trackings.map((tracking, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>
                            {tracking.status?.name || "Status"}
                          </TableCell>
                          <TableCell>
                            {formatDateTime(tracking.created_at)}
                          </TableCell>
                          <TableCell>{tracking.description || "-"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              ) : (
                <Typography variant="body2">No record found</Typography>
              )}
            </Grid>

            {/* Scanning History - Admin Only */}
            {selected.scans && selected.scans.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    p: 1,
                    bgcolor: theme.palette.action.hover,
                    borderRadius: 1,
                    color: theme.palette.text.primary,
                  }}
                >
                  Scanning History
                </Typography>
                <Paper variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date/Time</TableCell>
                        <TableCell>Branch</TableCell>
                        <TableCell>User</TableCell>
                        <TableCell>City</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selected.scans.map((scan, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>
                            {formatDateTime(scan.created_at)}
                          </TableCell>
                          <TableCell>{scan.branch?.name || "-"}</TableCell>
                          <TableCell>{scan.user?.name || "-"}</TableCell>
                          <TableCell>{scan.city?.name || "-"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </GenericModal>
  );
};

export default ConsignmentView;
